import React, { useState } from 'react';
import '../../ClientCard/ContentPages/Sales/Sales.scss';
import BemClass from '@upsales/components/Utils/bemClass';
import SimpleListView from '../../ClientCard/SimpleListView';
import SalesCardFilterHeader from '../../ClientCard/SimpleListView/SalesCardFilterHeader';
import Order from 'App/resources/Model/Order';
import RequestBuilder from 'Resources/RequestBuilder';
import OrderResource from 'App/resources/Order';
import OrderAttributes from 'App/babel/attributes/Order';
import ARRChangesAttributes from 'App/babel/attributes/ARRChangesAttributes';
import ComparisonTypes from 'Resources/ComparisonTypes';
import moment from 'moment';
import Agreement from 'App/resources/Model/Agreement';
import { Headline, Button, Text, Row } from '@upsales/components';
import AgreementAttributes from 'App/babel/attributes/AgreementAttributes';
import T from 'Components/Helpers/translate';
import { useSelector, useSoftDeployAccess } from 'App/components/hooks';
import { useSalesCardOrderFilters } from 'App/components/hooks/filters';
import { RootState } from 'Store/index';
import ARRChangeResource from 'App/babel/resources/ARRChanges';
import { ARRChange } from 'App/resources/Model/ARRChange';
import SubscriptionCards from 'App/components/SubscriptionCards';
import { CurrencyFormat } from 'App/babel/utils/numberFormat';
import ClientSales from 'App/components/ClientSales';
import ContactSales from './ContactSales';
import openModal from 'App/services/Modal/Modal';
import Client from 'App/resources/Model/Client';
import Contact from 'App/resources/Model/Contact';
import { openEditOrder } from 'App/components/EditOrder';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import SalesAddButton, { hasSubscriptionsAccess } from './SalesAddButton';
import { CardEntity } from './types';
import AppService from 'App/legacy/AppService';

const COLUMNS = ['date', 'description', 'contact', 'value', 'stage', 'user', 'delete'];
// A contact's own orders: the contact column is this contact on every row, and every row is a won
// order (`probability = 100`), so the stage barely varies. Angular's contact table showed neither
// (contactOrder.html - its stage cell is commented out and its rows carry no contact cell).
const COLUMNS_CONTACT = ['date', 'description', 'value', 'user', 'delete'];
const COLUMNS_AGREEMENT = ['startDate', 'nextOrderDate', 'description', 'contact', 'yearlyValue', 'user', 'delete'];

const getData = async (
	rb: RequestBuilder,
	cardEntity: CardEntity
): Promise<{
	data: Order[];
	metadata: {
		total: number;
	};
}> => {
	const excludedStages = await Tools.OrderStage.customer(AppService.getCustomerId()).find({ exclude: 1 });
	rb.addFilter(OrderAttributes.probability, ComparisonTypes.Equals, 100);
	rb.addFilter(
		OrderAttributes.stage,
		ComparisonTypes.NotEquals,
		excludedStages.data.map(stage => stage.id)
	);

	if (cardEntity.type === 'client') {
		const clientOr = rb.orBuilder();
		clientOr.next();
		clientOr.addFilter(OrderAttributes.account, ComparisonTypes.Equals, cardEntity.id);
		clientOr.next();
		clientOr.addFilter(OrderAttributes.clientConnection, ComparisonTypes.Equals, cardEntity.id);
		clientOr.done();
	} else {
		rb.addFilter({ field: 'contact.id' }, ComparisonTypes.Equals, cardEntity.id);
	}

	return OrderResource.find(rb.build());
};

// Clients only - a contact's subscriptions render as `SubscriptionCards`, which fetches its own.
const getAgreementData = (
	rb: RequestBuilder,
	{ clientId, inactiveAgreements }: { clientId: number; inactiveAgreements: boolean }
) => {
	rb.addFilter(AgreementAttributes.client, ComparisonTypes.Equals, clientId);

	if (inactiveAgreements) {
		rb.addFilter(AgreementAttributes.endDate, ComparisonTypes.LessThanEquals, moment().format('YYYY-MM-DD'));
	} else {
		const orBuilder = rb.orBuilder();
		orBuilder.next();
		orBuilder.addFilter(AgreementAttributes.endDate, ComparisonTypes.GreaterThan, moment().format('YYYY-MM-DD'));
		orBuilder.next();
		orBuilder.addFilter(AgreementAttributes.endDate, ComparisonTypes.Equals, null);
		orBuilder.done();
	}
	rb.addSort(AgreementAttributes.startDate);

	const rbBuild = rb.build();
	return Tools.Agreement.customer(AppService.getCustomerId()).find(rbBuild);
};

const generateSubHeader = (item: Order, sorting: string) => {
	switch (sorting) {
		case 'date':
			return moment(item.date).year().toString();
		case 'user.name':
			return item.user.name;
		case 'stage.id':
			return item.stage.name;
	}
	return '';
};

const generateSubHeaderAgreement = (item: Agreement, sorting: string) => {
	switch (sorting) {
		case 'metadata.agreementStartdate':
			return moment(item.metadata.agreementStartdate).isAfter()
				? T('agreement.futureAgreements')
				: T('defaultView.activeAgreements');
		case 'user.name':
			return item.user.name;
	}
	return '';
};

const generateSubHeaderARRChanges = (item: ARRChange, sorting: string) => {
	switch (sorting) {
		case 'date':
			return moment(item.date).year().toString();
		case 'type':
			return T(`arrchange.type.${item.type}`);
	}
	return '';
};

type DefaultCurrency = { iso: string; rate: number };

/**
 * The group total beside a sub-header, e.g. `2026` -> `(13 390 SEK)`.
 *
 * Ported from `getSumOfSales` (contactOrder.js): orders already in the default currency count
 * their own `value`, the rest are converted from the master currency at the default currency's
 * rate. Summing `value` blindly would add different currencies together.
 */
export const sumOrderGroup =
	(defaultCurrency: DefaultCurrency) =>
	(ordersInGroup: Order[]): React.ReactNode => {
		const total = ordersInGroup.reduce(
			(sum, order) =>
				sum +
				((order.currency || '').toLowerCase() === defaultCurrency.iso.toLowerCase()
					? order.value
					: Math.round(order.valueInMasterCurrency * defaultCurrency.rate)),
			0
		);

		return (
			<Text color="grey-10" size="lg">
				{` (${new CurrencyFormat(defaultCurrency.iso).short(total)})`}
			</Text>
		);
	};

const onRowClick =
	({ id }: Order) =>
	() => {
		openEditOrder({ id, type: 'order' });
	};

const onAgreementRowClick =
	({ id }: Agreement) =>
	() => {
		openAgreement({ agreementId: id });
	};

const SalesAgreementList = ({
	classes,
	inactiveAgreements,
	cardEntity
}: {
	classes: BemClass;
	inactiveAgreements: boolean;
	cardEntity: CardEntity;
}) => (
	<SimpleListView<Agreement>
		classes={classes}
		getData={rb => getAgreementData(rb, { clientId: cardEntity.id, inactiveAgreements })}
		columns={COLUMNS_AGREEMENT}
		subHeaderFn={inactiveAgreements ? undefined : generateSubHeaderAgreement}
		attributes={{
			...AgreementAttributes,
			description: {
				...AgreementAttributes.description,
				sortable: false
			},
			contact: {
				...AgreementAttributes.contact,
				placeholder: 'default.noContact'
			},
			nextOrderDate: {
				...AgreementAttributes.nextOrderDate,
				placeholder: 'agreement.noNextOrderDate'
			}
		}}
		rowStyles={{
			inactive: () => inactiveAgreements
		}}
		isDeletable={item => {
			return !!item.userRemovable;
		}}
		deleteFn={agreement => {
			return Tools.Agreement.customer(AppService.getCustomerId())['delete'](agreement);
		}}
		entityName={'default.agreement'}
		onRowClick={onAgreementRowClick}
		renderHeader={() => (
			<Row className="SimpleListView__header">
				<Headline size="sm">
					{inactiveAgreements ? T('default.agreementsInactive') : T('agreement.agreements')}
				</Headline>
				<div className="FlexPadder" />
				{!inactiveAgreements ? <SalesAddButton cardEntity={cardEntity} /> : null}
			</Row>
		)}
		broadcastType="order"
		initialSorting={[{ attribute: 'metadata.agreementStartdate', ascending: false }]}
		tableLimitOptions={[25]}
	/>
);

type SharedSalesProps = {
	cardEntity: CardEntity;
	// Only used for the client variant (ClientSales widgets) — the client card page pre-fetches it
	accountCardData?: any;
};

const SharedSales = ({ cardEntity, accountCardData }: SharedSalesProps) => {
	const classes = new BemClass('ClientCardContent__Sales');
	const salesData = accountCardData?.['sales']?.data;
	const isContact = cardEntity.type === 'contact';

	const [showInactiveAgreements, setShowInactiveAgreements] = useState(false);
	const hasSalesCardFilters = useSoftDeployAccess('SALES_CARD_FILTERS');
	const salesCardFilters = useSalesCardOrderFilters();

	const { SalesModel: salesModel, SalesModelOption: salesModelOption } = useSelector(
		(state: RootState) => state.App.metadata!.params
	);
	const defaultCurrency = useSelector((state: RootState) => state.App.metadata!.defaultCurrency);
	const hasContactSubscriptionCards = useSelector(({ App }) => hasSubscriptionsAccess(App));
	const [graphModel, setGraphModel] = useState<'recurring' | 'cashflow'>(
		salesModel === 'rr' ? 'recurring' : 'cashflow'
	);

	// The contact card has no recurring/cash-flow graph toggle (its chart is always order-based),
	// so it shows its order list unconditionally - matching the Angular contact sales tab, which
	// rendered the subscription cards and the per-order sales table side by side.
	const shouldShowAgreements = (salesModel === 'rr' && graphModel === 'recurring') || salesModel !== 'rr';
	const shouldShowOrders = isContact || (salesModel === 'rr' && graphModel === 'cashflow') || salesModel !== 'rr';

	const cmRatio = (salesData?.CMPast12Months ?? 0) / salesData?.salesPast12Months || 0;

	return (
		<div className={classes.b()}>
			{isContact ? <ContactSales contactId={cardEntity.id} /> : null}

			{cardEntity.type === 'client' ? (
				<ClientSales
					quotas={(cardEntity.data as Client).clientQuotas}
					clientId={cardEntity.id}
					setGraphType={t => setGraphModel(t)}
					arrChangeLast12Months={(cardEntity.data as Client).growth?.arrLast12Months ?? 0}
					mrrChangeLast12Months={(cardEntity.data as Client).growth?.mrrLast12Months ?? 0}
					currentARR={(cardEntity.data as Client).growth?.arr ?? 0}
					contributionMarginTotal={salesData?.CMPast12Months ?? 0}
					contributionMarginRatio={cmRatio}
					orderTotal={salesData?.salesPast12Months ?? 0}
					hideTarget
				/>
			) : null}

			{/* The contact's subscriptions use the same expandable cards the Angular contact card
			    rendered (contactOrder.html mounted this very component through react-driver), so
			    the heading carries the ARR sum, "Show inactive subscriptions" is a toggle beside
			    the action button, and each subscription shows its own start/renewal/next-order
			    dates. The client card keeps its sortable table - changing that is not this
			    ticket's call. */}
			{isContact && hasContactSubscriptionCards ? (
				// Angular sat the subscription cards in `#sales-tables`, whose `background-color:
				// $grey-1` (_account.scss) is what let each white card read as a card. The React
				// sales tab is `$white` throughout, so the cards had nothing to sit on. The
				// element only renders for a contact, so the client card keeps its own backdrop.
				<div className={classes.elem('contactSubscriptions').b()}>
					<SubscriptionCards
						// Its fetch runs once on mount, so a different contact needs a fresh instance
						key={cardEntity.id}
						client={(cardEntity.data as Contact).client as Client}
						contact={cardEntity.data as Contact}
						hasMRR={salesModelOption === 'mrr'}
					/>
				</div>
			) : null}

			{shouldShowAgreements && !isContact ? (
				<>
					<SalesAgreementList inactiveAgreements={false} cardEntity={cardEntity} classes={classes} />
					{showInactiveAgreements ? (
						<SalesAgreementList inactiveAgreements cardEntity={cardEntity} classes={classes} />
					) : null}
					<Button
						className={classes.elem('showInactiveAgreementToggler').b()}
						type="link"
						onClick={() => setShowInactiveAgreements(!showInactiveAgreements)}
					>{`${T(showInactiveAgreements ? 'default.hide' : 'default.show')} ${T(
						'default.agreementsInactive'
					)}`}</Button>

					{/* ARR changes are account-level movements (they open a drawer for the whole
					    company), so they belong on the client card only. On a contact they read as
					    that person's recurring movements, which they are not. */}
					<SimpleListView<ARRChange>
						classes={classes}
						getData={rb => {
							rb.addFilter(AgreementAttributes.client, ComparisonTypes.Equals, cardEntity.id);
							return ARRChangeResource.find(rb.build());
						}}
						columns={[
							'date',
							'type',
							salesModelOption === 'arr' ? 'annualValueInMasterCurrency' : 'monthlyValueInMasterCurrency',
							'users'
						]}
						subHeaderFn={generateSubHeaderARRChanges}
						attributes={ARRChangesAttributes}
						renderHeader={() => (
							<Headline size="sm">
								{T(salesModelOption === 'arr' ? 'default.arr' : 'default.mrr')}{' '}
								{T('default.changes').toLowerCase()}
							</Headline>
						)}
						onRowClick={change => () => {
							openModal('ARRChangeDrawerV2', {
								clientId: cardEntity.id,
								date: change.date,
								type: 'arrchange.type.' + change.type
							});
						}}
						broadcastType="order"
						initialSorting={[{ attribute: 'date', ascending: false }]}
						tableLimitOptions={[25]}
					/>
				</>
			) : null}

			{shouldShowOrders ? (
				<SimpleListView<Order>
					classes={classes}
					getData={rb => getData(rb, cardEntity)}
					columns={isContact ? COLUMNS_CONTACT : COLUMNS}
					subHeaderFn={generateSubHeader}
					subHeaderAggregateFn={isContact ? sumOrderGroup(defaultCurrency) : undefined}
					attributes={{
						...OrderAttributes,
						description: {
							...OrderAttributes.description,
							sortable: false
						},
						contact: {
							...OrderAttributes.contact,
							placeholder: 'default.noContact'
						}
					}}
					rowStyles={{
						inactive: item => item.probability === 0
					}}
					deleteFn={order => {
						return OrderResource.delete(order.id);
					}}
					isDeletable={order => {
						return !!order.userRemovable;
					}}
					entityName="default.order"
					onRowClick={onRowClick}
					filterConfigs={hasSalesCardFilters ? salesCardFilters : undefined}
					quickSearchFilter={hasSalesCardFilters ? 'ListSearch' : undefined}
					renderHeader={
						hasSalesCardFilters
							? provided => (
									<SalesCardFilterHeader
										provided={provided}
										title={<Headline size="sm">{T('default.orders')}</Headline>}
										extra={<SalesAddButton cardEntity={cardEntity} />}
									/>
							  )
							: () => (
									<Row className="SimpleListView__header">
										<Headline size="sm">{T('default.orders')}</Headline>
										<div className="FlexPadder" />
										<SalesAddButton cardEntity={cardEntity} />
									</Row>
							  )
					}
					broadcastType="order"
					initialSorting={[{ attribute: 'date', ascending: false }]}
					tableLimitOptions={[25]}
				/>
			) : null}
		</div>
	);
};

export default SharedSales;
